import { StudioApiError } from "@/types/api";
import { billingAccessDeniedMessage, isBillingAccessDenied } from "@/lib/http/billing-errors";

export type ApiErrorContext =
  | "account"
  | "customers"
  | "invoices"
  | "export"
  | "jobs"
  | "download"
  | "generic";

const CONTEXT_FALLBACK: Record<ApiErrorContext, string> = {
  account: "Could not connect to your Stripe account. Check your setup and try again.",
  customers: "We could not load customers. Try again in a moment.",
  invoices: "We could not load invoices. Check your filters and try again.",
  export: "We could not start the export. Try again or choose different invoices.",
  jobs: "We could not load exports. Try again in a moment.",
  download: "This download link is not available. Start a new export or open Exports.",
  generic: "Something went wrong. Please try again.",
};

export function formatApiError(
  error: unknown,
  context: ApiErrorContext = "generic",
): string {
  if (import.meta.env.DEV) {
    logTechnicalDetail(error, context);
  }

  if (error instanceof StudioApiError) {
    return mapStudioApiError(error, context);
  }

  return CONTEXT_FALLBACK[context];
}

function mapStudioApiError(error: StudioApiError, context: ApiErrorContext): string {
  switch (error.code) {
    case "ACCOUNT_NOT_READY":
      return "Your Stripe account is still loading. Wait a moment, then retry.";
    case "RATE_LIMITED":
      return "Stripe is busy. Please wait a few seconds and try again.";
    case "NOT_FOUND":
      return context === "jobs"
        ? "That export was not found. It may have expired."
        : CONTEXT_FALLBACK[context];
    case "ACCESS_DENIED":
      if (isBillingAccessDenied(error)) {
        return billingAccessDeniedMessage(error);
      }
      return CONTEXT_FALLBACK[context];
    default:
      return CONTEXT_FALLBACK[context];
  }
}

function logTechnicalDetail(error: unknown, context: ApiErrorContext): void {
  if (error instanceof StudioApiError) {
    console.debug(
      `[studio api:${context}]`,
      error.code,
      error.message,
      error.httpStatus,
      error.fieldErrors,
    );
    return;
  }
  if (error instanceof Error) {
    console.debug(`[studio api:${context}]`, error.message);
  }
}
